/**
 * Getty Favorites Context
 *
 * Provides state management for Getty vocabulary favorites.
 * Favorites are loaded on mount and cached for the session.
 */

import React, { createContext, ReactNode, useState, useRef, useCallback, useEffect, useContext } from 'react';
import {
  getGettyFavorites,
  addGettyFavorite as addGettyFavoriteApi,
  removeGettyFavorite as removeGettyFavoriteApi,
  VocabularyType,
  GettyFavoriteItem,
  GettyFavoritesResponse,
} from '../utils/gettyFavoritesApi';
import { useAuth } from '../hooks/useAuth';
import { ActiveCatalogContext } from './ActiveCatalogContext';
import { logger } from '../utils/logger';

interface GettyFavoritesContextValue {
  favorites: GettyFavoritesResponse;
  loading: boolean;
  error: string | null;
  isFavorite: (vocabularyType: VocabularyType, uri: string) => boolean;
  toggleFavorite: (
    vocabularyType: VocabularyType,
    uri: string,
    textValue: string,
    breadcrumbs: string[]
  ) => Promise<void>;
  getFavoritesForType: (vocabularyType: VocabularyType) => GettyFavoriteItem[];
}

const defaultFavorites: GettyFavoritesResponse = {
  workType: [],
  techniques: [],
  materials: [],
  substrate: [],
  signatureType: [],
  signatureLocation: [],
  titleLocation: [],
  dateLocation: [],
  updatedAt: null,
};

// eslint-disable-next-line react-refresh/only-export-components
export const GettyFavoritesContext = createContext<GettyFavoritesContextValue | undefined>(undefined);

export const GettyFavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const activeCatalogContext = useContext(ActiveCatalogContext);
  const activeCatalogOwnerId = activeCatalogContext?.activeCatalogOwnerId ?? null;
  const [favorites, setFavorites] = useState<GettyFavoritesResponse>(defaultFavorites);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the number of in-flight toggle operations so loadFavorites does
  // not overwrite optimistic state with stale server data (#980).
  const pendingToggleCount = useRef(0);

  // Load favorites on mount if authenticated, and reload when active catalog owner changes.
  // When viewing a delegated catalog (activeCatalogOwnerId set), fetch the owner's favorites
  // so the delegate sees the same favorites as the catalog owner (#952).
  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites(defaultFavorites);
      return;
    }

    const loadFavorites = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getGettyFavorites(activeCatalogOwnerId ?? undefined);
        // Skip overwriting state while a toggleFavorite API call is in-flight
        // to prevent a stale reload from erasing an optimistic update (#980).
        if (pendingToggleCount.current === 0) {
          setFavorites(data);
        }
      } catch (error) {
        setError('Failed to load favorites');
        logger.error('Failed to load Getty favorites', error);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [isAuthenticated, activeCatalogOwnerId]);

  const vocabularyTypeToKey = (type: VocabularyType): keyof GettyFavoritesResponse => {
    const map: Record<VocabularyType, keyof GettyFavoritesResponse> = {
      work_type: 'workType',
      techniques: 'techniques',
      materials: 'materials',
      substrate: 'substrate',
      signature_type: 'signatureType',
      signature_location: 'signatureLocation',
      title_location: 'titleLocation',
      date_location: 'dateLocation',
    };
    return map[type];
  };

  const isFavorite = useCallback(
    (vocabularyType: VocabularyType, uri: string): boolean => {
      const key = vocabularyTypeToKey(vocabularyType);
      const list = favorites[key] as GettyFavoriteItem[];
      return list.some((item) => item.uri === uri);
    },
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (
      vocabularyType: VocabularyType,
      uri: string,
      textValue: string,
      breadcrumbs: string[]
    ) => {
      const isCurrentlyFavorite = isFavorite(vocabularyType, uri);
      const key = vocabularyTypeToKey(vocabularyType);

      // Capture the pre-optimistic list for rollback purposes.
      // This snapshot is taken before any state update so it accurately
      // reflects what should be restored if the API call fails.
      const preOptimisticList = favorites[key] as GettyFavoriteItem[];

      // Optimistic update — use prev[key] inside the functional updater so
      // that concurrent toggles compose correctly even if React batches updates
      // and the closure's `favorites` is stale (#980).
      if (isCurrentlyFavorite) {
        setFavorites((prev) => ({
          ...prev,
          [key]: (prev[key] as GettyFavoriteItem[]).filter((item) => item.uri !== uri),
        }));
      } else {
        setFavorites((prev) => ({
          ...prev,
          [key]: [...(prev[key] as GettyFavoriteItem[]), { uri, textValue, breadcrumbs }],
        }));
      }

      // Increment counter so concurrent loadFavorites calls skip overwriting
      // our optimistic state with stale server data (#980).
      pendingToggleCount.current += 1;
      try {
        const ownerIdParam = activeCatalogOwnerId ?? undefined;
      const result = isCurrentlyFavorite
          ? await removeGettyFavoriteApi(vocabularyType, uri, ownerIdParam)
          : await addGettyFavoriteApi(vocabularyType, uri, textValue, breadcrumbs, ownerIdParam);
        setFavorites(result);
      } catch (error) {
        // Rollback to the pre-optimistic state for this vocabulary type only.
        setFavorites((prev) => ({ ...prev, [key]: preOptimisticList }));
        setError('Failed to update favorite');
        logger.error('Failed to toggle Getty favorite', error);
      } finally {
        pendingToggleCount.current -= 1;
      }
    },
    [favorites, isFavorite]
  );

  const getFavoritesForType = useCallback(
    (vocabularyType: VocabularyType): GettyFavoriteItem[] => {
      const key = vocabularyTypeToKey(vocabularyType);
      return favorites[key] as GettyFavoriteItem[];
    },
    [favorites]
  );

  return (
    <GettyFavoritesContext.Provider
      value={{
        favorites,
        loading,
        error,
        isFavorite,
        toggleFavorite,
        getFavoritesForType,
      }}
    >
      {children}
    </GettyFavoritesContext.Provider>
  );
};
